/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Label, Input, Button } from 'reactstrap';
import * as default_data from '../config.js'
import { setWithExpiry, getUser, getToken } from '../AuthService.js'
import { Alert, Divider, Chip, Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FaCopy, FaEye, FaEyeSlash, FaLongArrowAltLeft } from "react-icons/fa";
import { startAuthentication } from '@simplewebauthn/browser';
import { getBannerColor } from '../Utils/classification_helper.js';
import { getLoginLogo } from '../Utils/file_functions.js';

const QRCode = require('qrcode')

function MFAForm({login=false}) {

  const [qr, setQr] = useState("logo.png");
  const [secretKey, setSecretKey] = useState("");
  const [code, setCode] = useState("");
  const [hasMFA, setMFA] = useState(false);
  const [hasYubikey, setYubikey] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  var loaded_static = false;

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  function updateInfo() {
    var token = getToken()

    fetch(default_data.default_user_service_url_no_cac + '/get_mfa_secret', {
     method: 'POST',
     headers: {
       'Content-Type': 'application/json',
       'x-access-tokens': `${token}`
     },        
     body: JSON.stringify({
     })
   })
     .then(response => {
       if (response.ok) {
         return response.json();
       }
       throw new Error('An error occurred while sending chats.');
     })
     .then(data => {
       if(data.status != 200) {
           console.log(data.response);
           setErrorMessage('Internal error, please contact customer support');
       }
       else {        
          var user_obj = data.response;
          setYubikey(false)
          if(user_obj['webauthn_enabled'] && user_obj['webauthn_enabled'] === true) {
            setYubikey(true)
          }

          if(user_obj['mfa_secret'] === '' || user_obj['mfa_secret'] == null || user_obj['mfa_secret'].substring(0, 2) === 'T-') {
              setMFA(false)
              QRCode.toDataURL(user_obj['mfa_secret'].substring(2), (err, url) => {
                if (err) {
                  setErrorMessage('An error occurred while generating your QR code. Please try again.')
                }
                else {
                  setQr(url)
                  var url_obj = user_obj['mfa_secret'].substring(2);
                  url_obj = new URL(url_obj)
                  const secretKey = new URLSearchParams(url_obj.search).get('secret')
                  setSecretKey(secretKey)
                }
              })                          
          }
          else {
            setMFA(true)
          }
       }
      })
      .catch(error => {
  
      });  
  }

  useEffect(() => {
    if(loaded || loaded_static) {
      return;
    }

    setLoaded(true)
    loaded_static = true;

    var user = getUser()
     if(user == false) {
      window.location = '/login';
      return;
     }
     updateInfo()
  });

  const authenticateDevice = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
  
    const token = getToken();
    if (!token) {
      setErrorMessage('Authentication token missing. Please log in again.');
      setIsLoading(false);
      return;
    }
  
    try {
      // 1. Get login options (which includes the challenge)
      const resp = await fetch(
        default_data.default_user_service_url + '/webauthn-login-options',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-tokens': token
          }
        }
      );
      if (!resp.ok) throw new Error('Failed to get authentication options');
      const options = await resp.json();
  
      // 2. Ensure we have credentials to allow
      if (!options.allowCredentials || options.allowCredentials.length === 0) {
        throw new Error('No credentials available for authentication');
      }
  
      // 3. The challenge was added as "serverChallenge" in the server response
      const serverChallenge = options.serverChallenge;
      if (!serverChallenge) {
        throw new Error('No server challenge provided in the authentication options.');
      }
  
      // 4. Start client-side WebAuthn (navigator.credentials.get())
      const assertionResp = await startAuthentication(options);
  
      // 5. Verify the response on the server side, including the challenge in the header
      const verifyResp = await fetch(
        default_data.default_user_service_url + '/webauthn-login-verify',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-tokens': token,
            // Important: Send the challenge in a custom header
            'X-WebAuthn-Challenge': serverChallenge
          },
          body: JSON.stringify(assertionResp)
        }
      );
      if (!verifyResp.ok) {
        const err = await verifyResp.json().catch(() => ({}));
        throw new Error(err.message || 'Authentication failed');
      }
      var data = await verifyResp.json();
      // 6. If verification succeeded:
      setSuccessMessage('Authentication successful!');
      setWithExpiry('access_token', data.response.access_token);
      setWithExpiry('user', JSON.stringify(data.response.user));

      window.location.href = '/dashboard';        
} catch (err) {
      console.error(err);
      setErrorMessage('Authentication failed, please contact customer support.');
    } finally {
      setIsLoading(false);
    }
  };
    
  const handleSubmit = event => {
    event.preventDefault();
    setIsLoading(true);

    var token = getToken()
    if(token === false) {
      return;
    }

    if(login) {
      fetch(default_data.default_user_service_url_no_cac + '/login-mfa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },        
        body: JSON.stringify({
          code: code
        })
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('An error occurred while updating your account.');
        })
        .then(data => {
          setIsLoading(false);
          setSuccessMessage('')
          setErrorMessage('')
          if(data.status === 400) {
            setErrorMessage('Internal error, please contact customer support.');
            return;          
          }
          else if(data.status === 401) {
            setErrorMessage('Your MFA is not correctly setup, please reach out to customer support.');
            return;          
          }
          else if(data.status === 402) {
            setErrorMessage('Invalid code or expired, please try again.');
            return;          
          }
          else {
            setSuccessMessage("Login successful!");
            setWithExpiry('access_token', data.response.access_token);
            setWithExpiry('user', JSON.stringify(data.response.user));

            window.location.href = '/dashboard';        
          }
        })
        .catch(error => {
          setIsLoading(false);
          setErrorMessage("Internal error, please contact customer support");
        });
    }
    else {
      fetch(default_data.default_user_service_url_no_cac + '/update-mfa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },        
        body: JSON.stringify({
          code: code
        })
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('An error occurred while updating your account.');
        })
        .then(data => {
          setIsLoading(false);
          setSuccessMessage('')
          setErrorMessage('')
          if(data.status === 400) {
            setErrorMessage('Internal error, please contact customer support.');
            return;          
          }
          else if(data.status === 401) {
            setErrorMessage('Your MFA is already setup, if you need to update your account, please reach out to customer support.');
            return;          
          }
          else if(data.status === 402) {
            setErrorMessage('Invalid code, please try again.');
            return;          
          }
          else {
            setSuccessMessage("MFA is now setup!");

            const sleep = async (milliseconds) => {
              await new Promise(resolve => {
                  return setTimeout(resolve, milliseconds)
              });
            };
            const run_sleep = async () => {
              await sleep(3000);
              window.location.href = '/dashboard';        
            }
            run_sleep();
          }
        })
        .catch(error => {
          setIsLoading(false);
          setErrorMessage("Internal error, please contact customer support");
        });
      }
  };

  return (
    <div>
      { 
        <div className={`header-message ${getBannerColor(false)}`}>
          { default_data.default_banner_override != null && default_data.default_banner_override != '' ?
              default_data.default_banner_override : 'UNCLASSIFIED' 
          }
        </div>            
      }
      <div style={{  backgroundColor: '#121212', height: 'calc(100vh - 16px)'}}>
        <Stack sx={{maxHeight: 'calc(100vh - 16px)'}} direction={"row"} spacing={0}>
          <Box sx={{width: isTablet ? "100vw" : "50vw" , overflowY: 'auto'}} >
            <Container>
              <Box style={{display: 'flex', justifyContent: isTablet ? 'center' : 'start', marginBottom: '8px', marginTop: '32px'}}>
                <a href="/">
                  <img src="/logo.png" style={{width: isTablet ? '90px' : '120px'}} alt="Ask Sage" className="logo" />
                </a>
              </Box>
              <Row className="justify-content-center">
                <Col xs="12" md="8" lg="8">
                  <div>    
                    <h2 style={{ color: 'white', fontWeight: '500' }}>{login ? 'Multi-Factor Authentication Login' : 'Set up your MFA'}</h2>     
                    <Form onSubmit={handleSubmit} className='login-form'>
                      <div>
                        {
                          !hasMFA && login ? null :  
                          qr === 'logo.png' ? null
                          :
                          <div>
                            <p>Scan the QR Code in Microsoft Authenticator then enter the code that you see in the app in the text field and click Submit.</p>
                            <Box width={1} style={{justifyContent: 'center', display: 'flex'}}>
                              <img src={qr} alt="QRCode" style={{width: '200px'}} />
                            </Box>
                            <br />
                            {secretKey && (
                              <>
                                <Typography variant="body2" style={{color: 'white'}}>
                                  If you can't scan the QR code, you can manually enter the following code into the Microsoft Authenticator app:
                                </Typography>
                                <Box sx={{ width: '100%', mb: 3 }}>
                                  <Stack direction={"row"} spacing={1} alignItems="center" flexWrap="wrap">
                                    <IconButton onClick={() => setShowSecretKey(!showSecretKey)} style={{ color: 'white' }}>
                                      {showSecretKey ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                                    </IconButton>
                                    {showSecretKey && (
                                      <IconButton onClick={() => navigator.clipboard.writeText(secretKey)} style={{ color: 'white' }}>
                                        <FaCopy size={16} />
                                      </IconButton>
                                    )}
                                    <Typography
                                      variant="body2"
                                      style={{ color: 'white', wordWrap: 'break-word', flexGrow: 1 }}
                                    >
                                      {showSecretKey ? secretKey : '•'.repeat(secretKey.length)}
                                    </Typography>
                                    
                                  </Stack>
                                </Box>
                              </>
                            )}
                          </div>
                        }
                        { (hasMFA || !login) && (
                        <div>
                          <Label for="code">Code</Label>
                          <Input type="text" id="code" autoFocus={true} value={code} onChange={event => setCode(event.target.value)} name="code" />
                        </div>)}
                      </div>
                      <br />
                      {errorMessage && <Alert severity='error' sx={{mb: 2}}>{errorMessage}</Alert>}
                      {successMessage && <Alert severity='success' sx={{mb: 2}}>{successMessage}</Alert>}
                      { (hasMFA || !login) && (
                      <Button className='login-form-button' type="submit" disabled={isLoading}>
                        {isLoading ? 'Loading...' : 'Login with Authenticator'}
                      </Button>
                      )}
                      { ((hasMFA || !login) && login && hasYubikey && default_data.default_show_webauthn) && (
                        <Divider sx={{ my: 3, borderColor: 'white' }}>
                          <Chip label="OR" sx={{ color: 'white' }} />
                        </Divider>
                      )}
                      { login && hasYubikey && default_data.default_show_webauthn && (
                      <Button className='login-form-button' onClick={authenticateDevice} disabled={isLoading}>
                        {isLoading ? 'Loading...' : 'Login with Yubikey / Device'}
                      </Button>
                      )}
                      <Box mt={3} width={1} style={{textAlign: 'center'}}>
                        <a style={{color:'white', marginLeft: '8px'}} href="/dashboard">
                          <FaLongArrowAltLeft style={{marginRight: '8px'}}/>
                          Back
                        </a>
                      </Box>
                      <br /><br />
                    </Form>
                  </div>
                  </Col>
              </Row>
              <Row className="justify-content-center reg-page-mode-footer">
                <Col xs="12" md="12" lg="12">
                  Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
                </Col>
              </Row>
            </Container>
          </Box>
          {!isTablet && (
            <div
              className="space-image"
              style={{
                backgroundImage: "url('/space-background.png')",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                width: '50vw',
              }}
            >
              <img src={getLoginLogo()} alt="Space Girl" />
            </div>
          )}
        </Stack>

    </div>
    </div>
  );
}

export default MFAForm;
